import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { accountManagementService } from '@/services/accountManagementService'
import { awardBonusService } from '@/services/awardBonusService'
import type { AwardBonusSavePayload } from '@/api/types/awardBonus.types'
import type { DepartmentLookup, Employee } from '@/api/types'
import type { Account } from '@/services/accountManagementService'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const AwardBonusDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = id === 'new' || !id
  const editId = !isNew && id ? parseInt(id, 10) : 0

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [departmentId, setDepartmentId] = useState(0)
  const [employeeId, setEmployeeId] = useState<number | null>(null)
  const [bonusMonthYear, setBonusMonthYear] = useState(getDefaultMonthYear())
  const [debitAccountId, setDebitAccountId] = useState(0)
  const [amount, setAmount] = useState('')
  const [details, setDetails] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const req = t('awardBonusPage.requiredMark')

  // Department: same as Incentive – employeesService.getDepartmentLookups (branch selected)
  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Employees: same as Incentive – branch-wise, filter by department
  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setEmployeesList(res.data)
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Debit accounts: same as Incentive – accountManagementService.getAllAccounts
  useEffect(() => {
    if (!branchId) {
      setAccounts([])
      return
    }
    let cancelled = false
    accountManagementService.getAllAccounts(branchId).then((list) => {
      if (!cancelled) setAccounts(list)
    }).catch(() => { if (!cancelled) setAccounts([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Load record in edit mode
  useEffect(() => {
    if (isNew || !branchId || !editId) return
    let cancelled = false
    setLoading(true)
    awardBonusService.getById(editId, branchId).then((data) => {
      if (!cancelled) {
        setEmployeeId(data.employeeId != null ? Number(data.employeeId) : 0)
        setDepartmentId(data.departmentId ?? 0)
        setBonusMonthYear(data.bonusMonthYear ?? data.salaryMonthYear ?? getDefaultMonthYear())
        setDebitAccountId(data.debitAccount ?? 0)
        setAmount(String(data.amount ?? ''))
        setDetails(data.details ?? '')
      }
    }).catch((err) => {
      if (!cancelled) {
        toast.error(err.response?.data?.message || t('awardBonusPage.loadFailed'))
        navigate('/payroll/award-bonus')
      }
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [isNew, branchId, editId, navigate])

  const departmentOptions = useMemo(
    () => [
      { value: '0', label: t('awardBonusPage.placeholderAll') },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || String(d.id),
      })),
    ],
    [departments, t]
  )

  const employeeOptions = useMemo(() => {
    const nameFor = (emp: Employee) =>
      [emp.empFirstNameEn, emp.empFamilyNameEn].filter(Boolean).join(' ').trim() || emp.empCode || '—'
    const list = departmentId
      ? employeesList.filter((e) => e.empDepartmetId === departmentId && e.id && e.empCode)
      : employeesList.filter((e) => e.id && e.empCode)
    return [
      { value: '0', label: t('awardBonusPage.allEmployees') },
      ...list.map((e) => ({ value: String(e.id), label: nameFor(e) })),
    ]
  }, [employeesList, departmentId, t])

  const debitAccountOptions = useMemo(
    () =>
      accounts.map((acc) => {
        const code = acc.accountCode || ''
        const name = acc.accountNameEn || acc.accountName || acc.accountNameAr || t('awardBonusPage.accountFallback', { id: acc.id })
        return { value: String(acc.id || 0), label: code ? `${code} - ${name}` : name }
      }),
    [accounts, t]
  )

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('awardBonusPage.toastBranch'))
      return
    }
    if (employeeId === null) {
      toast.error(t('awardBonusPage.toastEmployee'))
      return
    }
    if (!bonusMonthYear || bonusMonthYear.length < 7) {
      toast.error(t('awardBonusPage.toastBonusMonth'))
      return
    }
    if (!debitAccountId) {
      toast.error(t('awardBonusPage.toastDebitAccount'))
      return
    }
    const amt = parseFloat(amount)
    if (Number.isNaN(amt) || amt < 0) {
      toast.error(t('awardBonusPage.toastAmount'))
      return
    }
    if (!details.trim()) {
      toast.error(t('awardBonusPage.toastDetails'))
      return
    }

    setSaving(true)
    try {
      const payload: AwardBonusSavePayload = {
        id: isNew ? 0 : editId,
        empId: employeeId,
        bonusAmount: amt,
        monthYear: bonusMonthYear,
        detail: details.trim(),
        departmentId: departmentId || 0,
        debitAccount: debitAccountId,
        salaryMonthYear: bonusMonthYear,
        branchId,
      }
      console.log('=== Award Bonus API Payload (Save/Update) ===')
      console.log(JSON.stringify(payload, null, 2))
      console.log('============================================')
      if (isNew) {
        await awardBonusService.save(payload)
        await showSuccess(t('awardBonusPage.savedTitle'), t('awardBonusPage.savedMessage'))
      } else {
        await awardBonusService.update(payload)
        await showSuccess(t('awardBonusPage.updatedTitle'), t('awardBonusPage.updatedMessage'))
      }
      navigate('/payroll/award-bonus')
    } catch (err: any) {
      const data = err?.response?.data
      const msg =
        data?.result?.msg ??
        data?.Result?.msg ??
        data?.message ??
        err?.message ??
        t('awardBonusPage.saveFailed')
      await showError(t('awardBonusPage.saveFailed'), msg)
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('awardBonusPage.pageTitle')} description={t('awardBonusPage.pageDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('awardBonusPage.selectBranchForm')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('awardBonusPage.pageTitle')} description={t('awardBonusPage.pageDescription')} />

      <Card>
        <CardHeader>
          <CardTitle>{t('awardBonusPage.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">{t('awardBonusPage.loading')}</div>
          ) : (
            <>
              {/* Row 1: Department, Employee, Bonus Month/Year */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{req} {t('awardBonusPage.labelDepartment')}</Label>
                  <SearchableSelect
                    options={departmentOptions}
                    value={departmentId ? String(departmentId) : '0'}
                    onValueChange={(v) => {
                      setDepartmentId(v ? Number(v) : 0)
                      setEmployeeId(null)
                    }}
                    placeholder={t('awardBonusPage.placeholderAll')}
                    searchPlaceholder={t('awardBonusPage.searchShort')}
                    emptyMessage={t('awardBonusPage.emptyDepartment')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('awardBonusPage.labelEmployee')}</Label>
                  <SearchableSelect
                    options={employeeOptions}
                    value={employeeId === null ? '' : String(employeeId)}
                    onValueChange={(v) => setEmployeeId(v === '' ? null : Number(v))}
                    placeholder={t('awardBonusPage.placeholderSelectEmployee')}
                    searchPlaceholder={t('awardBonusPage.searchShort')}
                    emptyMessage={t('awardBonusPage.emptyEmployee')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('awardBonusPage.labelBonusMonthYear')}</Label>
                  <Input
                    type="month"
                    value={bonusMonthYear}
                    onChange={(e) => setBonusMonthYear(e.target.value)}
                  />
                </div>
              </div>

              {/* Row 2: Debit Account, Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{req} {t('awardBonusPage.labelDebitAccount')}</Label>
                  <SearchableSelect
                    options={debitAccountOptions}
                    value={debitAccountId ? String(debitAccountId) : ''}
                    onValueChange={(v) => setDebitAccountId(v ? Number(v) : 0)}
                    placeholder={t('awardBonusPage.placeholderSelectAccount')}
                    searchPlaceholder={t('awardBonusPage.searchAccounts')}
                    emptyMessage={t('awardBonusPage.emptyAccounts')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('awardBonusPage.labelAmount')}</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder={t('awardBonusPage.placeholderAmount')}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>

              {/* Row 4: Details */}
              <div className="space-y-2">
                <Label>{req} {t('awardBonusPage.labelDetails')}</Label>
                <Textarea
                  placeholder={t('awardBonusPage.placeholderDetails')}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => navigate('/payroll/award-bonus')}>
                  {t('awardBonusPage.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? t('awardBonusPage.saving') : t('awardBonusPage.save')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
